import { supabase, currentAccessToken } from './supabase.js'
import { ERR, FATAL_SESSION, SUBSCRIPTION_BLOCK, isUiBug } from './errors.js'

/**
 * The MBOS API client (Phase 1b §3.2).
 *
 * ── ONE MODULE TOUCHES fetch, AND THIS IS IT ────────────────────────────────
 * D5 chose plain JavaScript with JSDoc over TypeScript, which means nothing
 * mechanically stops a component from inventing a response field. The
 * mitigation is structural rather than syntactic: this is the ONLY module in
 * the app that calls `fetch`, so a field that does not exist here does not
 * exist anywhere. Every response shape below is documented as a typedef, and
 * those typedefs are the app's type system.
 *
 * Adding a `fetch` call anywhere else defeats the entire arrangement.
 *
 * ── WHAT IT GUARANTEES ──────────────────────────────────────────────────────
 *   · the bearer token is attached to every call, read live so a background
 *     refresh is picked up immediately;
 *   · every failure becomes an ApiError carrying a STABLE SLUG, so callers
 *     branch on `err.slug === ERR.NOT_DRAFT` and never on prose;
 *   · a 401 gets exactly one silent refresh-and-retry before the session is
 *     abandoned — no retry loop, no flicker;
 *   · a network failure is distinguishable from a server rejection, because a
 *     contractor in a basement needs different words than one hitting a 409.
 */

const BASE_URL = import.meta.env.VITE_API_URL

if (!BASE_URL) {
  throw new Error(
    'Missing VITE_API_URL. Copy dashboard/.env.example to dashboard/.env.local. ' +
    'Point it at a tenant or dashboard hostname — NOT plain localhost, which ' +
    'resolves to no tenant and 404s every authenticated call.'
  )
}

/**
 * A failed API call. Always carries a slug; `status` is 0 when the request
 * never reached the server at all.
 */
export class ApiError extends Error {
  /**
   * @param {number} status
   * @param {string} slug
   * @param {string} message
   * @param {object} [body] the full response body, for slugs that carry extras
   */
  constructor (status, slug, message, body = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.slug = slug
    this.body = body
  }

  /** `feature_not_enabled` carries these, for the upgrade prompt. */
  get feature () { return this.body.feature ?? null }
  get tier () { return this.body.tier ?? null }
}

/** Callbacks the auth layer registers so this module never imports React. */
const handlers = { onSessionLost: null, onSubscriptionBlocked: null }

/**
 * Register what to do when a session ends, or when the subscription gate closes
 * mid-request.
 * @param {{onSessionLost?: () => void, onSubscriptionBlocked?: () => void}} next
 */
export function configureApi (next) {
  Object.assign(handlers, next)
}

/**
 * Perform an API request.
 *
 * @param {string} path e.g. '/api/jobs'
 * @param {object} [options]
 * @param {'GET'|'POST'|'PATCH'|'DELETE'} [options.method]
 * @param {object} [options.body]
 * @param {Record<string, string|number|undefined|null>} [options.query]
 * @param {boolean} [options.auth] attach a bearer token (default true)
 * @param {'json'|'blob'} [options.responseType] successful response body type
 * @param {boolean} [_isRetry] internal — prevents more than one refresh attempt
 * @returns {Promise<any>}
 * @throws {ApiError}
 */
export async function apiFetch (path, options = {}, _isRetry = false) {
  const { method = 'GET', body, query, auth = true, responseType = 'json' } = options

  const url = new URL(path, BASE_URL)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }

  const headers = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  if (auth) {
    const token = await currentAccessToken()
    if (!token) {
      // No session at all. Not worth a round trip to be told so.
      handlers.onSessionLost?.()
      throw new ApiError(401, ERR.MISSING_TOKEN, 'You are signed out.')
    }
    headers.Authorization = `Bearer ${token}`
  }

  let response
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // No cookies, ever (Phase 0 §2). Also what keeps a misconfigured CORS
      // policy from appearing to work in development and failing in production.
      credentials: 'omit'
    })
  } catch {
    // fetch only rejects when the request never completed: offline, DNS,
    // or CORS refusing before the call was made.
    throw new ApiError(
      0,
      ERR.NETWORK,
      'Could not reach MBOS. Check your connection and try again.'
    )
  }

  if (response.status === 204) return null

  // PDF template previews are deliberately binary. Errors still pass through
  // the JSON body path below, so stable slugs and the one-refresh rule remain
  // exactly the same as every other endpoint.
  if (response.ok && responseType === 'blob') return await response.blob()

  const payload = await readBody(response)

  if (response.ok) return payload

  const slug = payload?.error ?? ERR.REQUEST_FAILED
  const message = payload?.message ?? 'That request could not be completed.'

  // ── 401: exactly one silent refresh, then give up ────────────────────────
  // Supabase normally refreshes ahead of expiry on its own; this covers the
  // case where it did not — a laptop that was asleep, most often. One attempt,
  // because a token the server has already rejected twice will not start
  // working, and looping produces a login screen that flickers.
  if (response.status === 401 && auth && !_isRetry) {
    const { data, error } = await supabase.auth.refreshSession()
    if (!error && data.session) {
      return apiFetch(path, options, true)
    }
    handlers.onSessionLost?.()
    throw new ApiError(response.status, slug, message, payload ?? {})
  }

  if (response.status === 401) {
    handlers.onSessionLost?.()
  }

  // A token belonging to another workspace is a stale session, never a retry.
  if (FATAL_SESSION.includes(slug)) {
    handlers.onSessionLost?.()
  }

  // ── 402: the subscription gate closed while the app was open ─────────────
  // A webhook can suspend a workspace at any moment, so this arrives on an
  // ordinary call with no warning. The boot payload the shell is rendering
  // from is now stale, which matters: without this the user keeps clicking a
  // dashboard where every action fails and nothing explains why. Re-reading
  // /api/me/features is what flips the shell to the blocked screen.
  if (response.status === 402 && SUBSCRIPTION_BLOCK.includes(slug)) {
    handlers.onSubscriptionBlocked?.()
  }

  const err = new ApiError(response.status, slug, message, payload ?? {})

  if (isUiBug(err) && import.meta.env.DEV) {
    // These mean this code is wrong, not that the user did something odd.
    // Loud in development so they surface during the build.
    console.error(
      `[api] ${method} ${path} returned "${slug}", which indicates a bug in the ` +
      'dashboard rather than user error.', err
    )
  }

  throw err
}

/** Parse a response body without throwing on an empty or non-JSON one. */
async function readBody (response) {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    // A non-JSON body means something upstream answered instead of the API —
    // a proxy error page, most likely. Surface it as an internal error rather
    // than crashing on the parse.
    return { error: ERR.INTERNAL_ERROR, message: 'The server returned an unexpected response.' }
  }
}

/* ==========================================================================
   Typed endpoint wrappers.

   Session 1 ships only what the shell needs. Screens land in session 2 and add
   their own wrappers here — NOT their own fetch calls.
   ========================================================================== */

/**
 * @typedef {object} Branding
 * @property {string} [logo_url]
 * @property {string} [primary_color]
 * @property {string} [secondary_color]
 * @property {string} [business_name]
 * @property {string} [favicon_url]
 * @property {string} [font_preference]
 *
 * @typedef {object} Tenant
 * @property {string} id
 * @property {string} slug
 * @property {string} name
 * @property {'starter'|'growth'|'pro'} tier
 * @property {'trial'|'active'|'past_due'|'suspended'|'canceled'} status
 * @property {Branding} branding
 *
 * @typedef {object} SessionUser
 * @property {string} id
 * @property {string|null} email
 * @property {'owner'|'admin'|'member'|null} role
 * @property {boolean} operations_access  Phase 9 D9.1 — RENDERING ONLY. The
 *   boundary is the server gate on every /api/operations/* call; this decides
 *   whether the navigation group is drawn.
 *
 * @typedef {object} MeFeatures
 * @property {Tenant} tenant
 * @property {SessionUser} user
 * @property {Record<string, boolean>} features
 */

/**
 * The call that boots the app shell: identity, tier, branding and flags in one
 * request (Phase 1b §3.3).
 * @returns {Promise<MeFeatures>}
 */
export const getMe = () => apiFetch('/api/me/features')

/**
 * @typedef {object} PipelineStage
 * @property {string} id
 * @property {string} name
 * @property {string|null} description
 * @property {number} stage_order
 * @property {string} color
 * @property {string|null} icon
 * @property {boolean} is_won_stage
 * @property {boolean} is_lost_stage
 * @property {boolean} is_active
 */

/** @returns {Promise<{pipeline_stages: PipelineStage[]}>} */
export const getPipelineStages = () => apiFetch('/api/pipeline-stages')

/**
 * @typedef {object} WorkspaceUser
 * @property {string} id
 * @property {string} first_name
 * @property {string|null} last_name
 * @property {string} email
 * @property {string|null} phone
 * @property {string|null} avatar_url
 * @property {boolean} is_active
 * @property {boolean} is_owner
 * @property {'owner'|'admin'|'member'|null} role                membership role
 * @property {boolean} operations_access                         Phase 9 D9.1
 * @property {boolean} operations_access_locked  true for the owner, whose
 *   access follows their role and cannot be switched off
 */

/** @returns {Promise<{users: WorkspaceUser[]}>} */
export const getUsers = () => apiFetch('/api/users')

/**
 * Grant or revoke the Operations zone for another login (Phase 9 D9.1).
 *
 * OWNER ONLY — a non-owner receives 403 `not_permitted`. Targeting the owner's
 * own membership answers 409 `operations_access_owner_implicit`, because their
 * access is derived from their role and there is no stored grant to move.
 *
 * Takes effect on the target's VERY NEXT request: access is read live from
 * tenant_users per request (D24.6/C14), never carried in their JWT, so a
 * revocation does not wait for a token refresh.
 *
 * @param {string} userId
 * @param {boolean} operationsAccess
 * @returns {Promise<{membership: {user_id: string, role: string, operations_access: boolean, operations_access_locked: boolean}}>}
 */
export const setUserOperationsAccess = (userId, operationsAccess) =>
  apiFetch(`/api/users/${userId}/operations-access`, {
    method: 'PATCH',
    body: { operations_access: operationsAccess }
  })

/** @returns {Promise<{seats: {used:number, limit:number, remaining:number}}>} */
export const getUserSeats = () => apiFetch('/api/users/seats')

/** @param {{email:string, first_name?:string, last_name?:string, role?:'admin'|'member'}} body */
export const inviteUser = body => apiFetch('/api/users/invite', { method: 'POST', body })

/**
 * @typedef {object} FormKey
 * @property {string} id
 * @property {string} key
 * @property {string|null} label
 * @property {string[]} allowed_origins
 * @property {boolean} is_active
 * @property {string} created_at
 * @property {string|null} revoked_at
 */

/** @returns {Promise<{form_keys: FormKey[]}>} */
export const getFormKeys = () => apiFetch('/api/form-keys')

/**
 * Set which websites may submit through a form key (D3).
 *
 * The ONLY field this endpoint accepts — sending anything else is a 400 by
 * design rather than a silent ignore. Bare hostnames are canonicalised
 * server-side, so 'example.com' is accepted and stored as
 * 'https://example.com'.
 *
 * @param {string} id
 * @param {string[]} allowedOrigins
 * @returns {Promise<{form_key: FormKey}>}
 */
export const updateFormKeyOrigins = (id, allowedOrigins) =>
  apiFetch(`/api/form-keys/${id}`, {
    method: 'PATCH',
    body: { allowed_origins: allowedOrigins }
  })

/* --------------------------------------------------------------------------
   Tenant settings (Phase 2 item 16a — §5 items 6a + 6c)

   The two endpoints that were specified alongside their column-level grants
   and never written. `tenants` grants UPDATE on exactly five columns, so
   anything not listed in these two calls is refused by the DATABASE, not by
   the router.
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} PublicProfile
 * @property {string} [business_phone]
 * @property {string} [business_email]
 * @property {string} [address]
 * @property {string} [license_number]
 * @property {string} [hours]
 * @property {string} [website]
 *
 * @typedef {object} DepositSettings
 * @property {number} deposit_percent           0-100
 * @property {number} deposit_minimum_cents     integer cents
 * @property {'single'|'split'} default_billing_mode
 * @property {number|null} change_order_threshold_cents  null = hint switched off
 *
 * @typedef {object} TenantSettings
 * @property {DepositSettings} deposit
 * @property {PublicProfile} public_profile   {} when nothing is published
 */

/** @returns {Promise<TenantSettings>} */
export const getSettings = () => apiFetch('/api/settings')

/**
 * D22.1. A minimum below the processor floor is refused with
 * `deposit_minimum_below_floor` — check it client-side too, because a form
 * that submits and then fails is worse than one that will not submit.
 * @param {Partial<DepositSettings>} body
 * @returns {Promise<TenantSettings>}
 */
export const updateDepositSettings = body =>
  apiFetch('/api/settings/deposit', { method: 'PATCH', body })

/**
 * C10. Send the WHOLE profile every time — this replaces rather than merges,
 * so omitting a key clears it. An empty object removes the contact block
 * entirely, which is the safe state and an expected one.
 *
 * Unknown keys are refused with `invalid_field` rather than dropped, so a
 * typo cannot look like it saved.
 * @param {PublicProfile} publicProfile
 * @returns {Promise<TenantSettings>}
 */
export const updatePublicProfile = publicProfile =>
  apiFetch('/api/settings/public-profile', { method: 'PATCH', body: { public_profile: publicProfile } })

/* --------------------------------------------------------------------------
   Payments boarding (Phase 2 §4.2 — the Settings payments card)

   Built in Session 1a with nothing calling it until item 16a.
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} BoardingRequirement
 * @property {string} section   business | owners | banking | processing | other
 * @property {string} label     MBOS's own copy. Maverick's strings are never echoed (G8).
 *
 * @typedef {object} RequiredDocument
 * @property {string} name
 * @property {boolean} uploaded
 * @property {boolean} action_required
 *
 * @typedef {object} OnboardingStatus
 * @property {'not_connected'|'pending'|'active'|'disabled'} maverick_status
 * @property {{application_id: string, application_status: string,
 *             signature_status: string|null, created_at: string,
 *             approved_at: string|null, declined_reason: string|null}|null} application
 * @property {BoardingRequirement[]} still_needed       empty when Maverick is unreachable
 * @property {RequiredDocument[]} required_documents    empty when Maverick is unreachable
 */

/** @returns {Promise<OnboardingStatus>} */
export const getOnboardingStatus = () => apiFetch('/api/payments/onboarding')

/**
 * Create or resolve the boarding application and mint a fresh hosted URL.
 *
 * The URL is a BEARER CREDENTIAL for the application — never log it, never
 * store it, and open it in a new tab rather than an iframe. Calling this twice
 * is safe and returns the same application (D16).
 *
 * @returns {Promise<{application_id: string, url: string}>}
 */
export const startOnboarding = () =>
  apiFetch('/api/payments/onboarding', { method: 'POST' })

/* --------------------------------------------------------------------------
   Billing (Phase 0b)

   These three are the ONLY routes that stay reachable when the subscription
   gate is closed, alongside /api/me/features. If a call here starts 402ing,
   the exemption in routes/index.js has been broken.
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} BillingStatus
 * @property {'trial'|'active'|'past_due'|'suspended'|'canceled'} status
 * @property {'starter'|'growth'|'pro'} tier
 * @property {boolean} has_subscription
 * @property {boolean} can_open_portal   false → no Stripe customer, so the
 *                                       portal button must not be offered
 * @property {boolean} access_blocked    mirrors requireActiveBilling exactly
 * @property {boolean} needs_attention   past_due | suspended | canceled
 */

/** @returns {Promise<{billing: BillingStatus}>} */
export const getBillingStatus = () => apiFetch('/api/billing/status')

/**
 * Open the Stripe-hosted customer portal.
 *
 * Returns a URL to NAVIGATE to — never render it in an iframe. Stripe sets
 * frame-ancestors, so an iframe silently renders nothing, and the customer sees
 * a blank panel where their card form should be.
 *
 * @returns {Promise<{url: string}>}
 */
export const createPortalSession = () =>
  apiFetch('/api/billing/portal-session', { method: 'POST' })

/**
 * Start a subscription through Stripe-hosted Checkout.
 * @param {'starter'|'growth'|'pro'} tier
 * @returns {Promise<{url: string}>}
 */
export const createCheckoutSession = tier =>
  apiFetch('/api/billing/checkout-session', { method: 'POST', body: { tier } })

/* --------------------------------------------------------------------------
   Clients
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} Client
 * @property {string} id
 * @property {string} first_name
 * @property {string|null} last_name   nullable by design — Phase 1 D3
 * @property {string|null} email
 * @property {string|null} phone
 * @property {string|null} address
 * @property {string|null} city
 * @property {string|null} state
 * @property {string|null} zip
 * @property {string|null} property_address
 * @property {string|null} notes
 * @property {boolean} do_not_contact
 * @property {string} created_at
 */

/**
 * @param {{search?: string, limit?: number, offset?: number}} [params]
 * @returns {Promise<{clients: Client[]}>}
 *
 * NOTE: no total count is returned (C7), so numbered pagination is not
 * expressible — the list uses load-more. See D10.
 */
export const getClients = (params = {}) => apiFetch('/api/clients', { query: params })

/** @returns {Promise<{client: Client, jobs: Job[]}>} */
export const getClient = id => apiFetch(`/api/clients/${id}`)

/** @param {Partial<Client>} body @returns {Promise<{client: Client}>} */
export const createClient = body => apiFetch('/api/clients', { method: 'POST', body })

/** @param {string} id @param {Partial<Client>} body @returns {Promise<{client: Client}>} */
export const updateClient = (id, body) => apiFetch(`/api/clients/${id}`, { method: 'PATCH', body })

/* --------------------------------------------------------------------------
   Jobs
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} EmbeddedClient
 * @property {string} id
 * @property {string} first_name
 * @property {string|null} last_name
 *
 * @typedef {object} Job
 * @property {string} id
 * @property {string} client_id
 * @property {string|null} pipeline_stage_id
 * @property {string|null} assigned_user_id
 * @property {string} job_number
 * @property {string} title
 * @property {string|null} description
 * @property {string} job_type
 * @property {'active'|'won'|'lost'|'on_hold'|'cancelled'} status
 * @property {'low'|'normal'|'high'|'urgent'} priority
 * @property {number} estimated_value
 * @property {string|null} notes
 * @property {string} created_at
 * @property {EmbeddedClient|null} clients  embedded so a board card needs no extra fetch
 */

/**
 * @param {{stage_id?: string, status?: string, assigned_user_id?: string, client_id?: string}} [filters]
 * @returns {Promise<{jobs: Job[]}>}
 */
export const getJobs = (filters = {}) => apiFetch('/api/jobs', { query: filters })

/**
 * @typedef {object} StageHistoryEntry
 * @property {string} id
 * @property {string|null} from_stage_id
 * @property {string} to_stage_id
 * @property {string|null} changed_by
 * @property {string|null} reason
 * @property {string} created_at
 * @property {{id: string, name: string}|null} from_stage
 * @property {{id: string, name: string}|null} to_stage
 *
 * @typedef {object} JobDetail
 * @property {Job} job
 * @property {EstimateSummary[]} estimates
 * @property {Appointment[]} appointments
 * @property {{id: string, user_id: string, role_on_job: string, assigned_at: string}[]} assignments
 * @property {StageHistoryEntry[]} stage_history  newest first
 */

/** @returns {Promise<JobDetail>} */
export const getJob = id => apiFetch(`/api/jobs/${id}`)

/** @param {Partial<Job>} body @returns {Promise<{job: Job}>} */
export const createJob = body => apiFetch('/api/jobs', { method: 'POST', body })

/**
 * @param {string} id
 * @param {Partial<Job>} body
 * @returns {Promise<{job: Job}>}
 *
 * Does NOT accept pipeline_stage_id — the API answers 400 `use_stage_endpoint`,
 * because two paths to a stage change means one of them skips the audit trail.
 * Use moveJobStage.
 */
export const updateJob = (id, body) => apiFetch(`/api/jobs/${id}`, { method: 'PATCH', body })

/**
 * The ONLY way to change a job's stage. Writes job_stage_history.
 * @param {string} id
 * @param {string} stageId
 * @param {string} [reason]
 * @returns {Promise<{job: Job, stage: PipelineStage, moved: boolean}>}
 */
export const moveJobStage = (id, stageId, reason) =>
  apiFetch(`/api/jobs/${id}/stage`, { method: 'POST', body: { stage_id: stageId, reason } })

/** @returns {Promise<{assignment: object}>} */
export const assignCrew = (jobId, userId, roleOnJob = 'support') =>
  apiFetch(`/api/jobs/${jobId}/assignments`, {
    method: 'POST',
    body: { user_id: userId, role_on_job: roleOnJob }
  })

export const unassignCrew = (jobId, userId) =>
  apiFetch(`/api/jobs/${jobId}/assignments/${userId}`, { method: 'DELETE' })

/* --------------------------------------------------------------------------
   Estimates
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} LineItem
 * @property {string} description
 * @property {number} quantity
 * @property {number} unit_price
 * @property {number} total     COMPUTED SERVER-SIDE. Anything sent is ignored.
 * @property {boolean} taxable
 *
 * @typedef {object} EstimateSummary
 * @property {string} id
 * @property {string} estimate_number
 * @property {'draft'|'sent'|'viewed'|'accepted'|'declined'|'expired'} status
 * @property {number} total
 * @property {string|null} sent_at
 * @property {string|null} accepted_at
 * @property {string} created_at
 *
 * @typedef {EstimateSummary & {
 *   job_id: string,
 *   line_items: LineItem[],
 *   subtotal: number,
 *   discount_type: 'percentage'|'flat'|null,
 *   discount_value: number,
 *   discount_amount: number,
 *   tax_rate: number,
 *   tax_amount: number,
 *   notes_client: string|null,
 *   notes_internal: string|null,
 *   accepted_by_name: string|null,
 *   declined_at: string|null,
 *   valid_until: string|null
 * }} Estimate
 */

/** @returns {Promise<{estimates: Estimate[]}>} */
export const getEstimates = (filters = {}) => apiFetch('/api/estimates', { query: filters })

/**
 * @typedef {object} SignatureStatus
 * @property {string} document_id
 * @property {string} signed_at
 * @property {'remote'|'in_person'} capture_mode
 * @property {string|null} facilitated_by  tenant user id — in_person only (D27.5)
 * @property {string|null} signer_name
 * @property {string|null} mark_type       'signature' | 'initials' (D27.4)
 * @property {string} document_hash
 *
 * `signature` is null until the estimate is signed. It carries METADATA only —
 * the captured signature image stays on the signed PDF and never rides this
 * response.
 *
 * @returns {Promise<{estimate: Estimate, signature: SignatureStatus|null}>}
 */
export const getEstimate = id => apiFetch(`/api/estimates/${id}`)

/**
 * Totals are computed server-side from line_items — never send a `total`.
 * @returns {Promise<{estimate: Estimate}>}
 */
export const createEstimate = body => apiFetch('/api/estimates', { method: 'POST', body })

/** Draft only. A sent estimate answers 409 `not_draft`. */
export const updateEstimate = (id, body) =>
  apiFetch(`/api/estimates/${id}`, { method: 'PATCH', body })

export const sendEstimate = id => apiFetch(`/api/estimates/${id}/send`, { method: 'POST' })

/**
 * Phase 1's click-accept. Real e-signature is Phase 2 — this records who
 * accepted and when, and nothing more.
 */
export const acceptEstimate = (id, acceptedByName) =>
  apiFetch(`/api/estimates/${id}/accept`, { method: 'POST', body: { accepted_by_name: acceptedByName } })

export const declineEstimate = id => apiFetch(`/api/estimates/${id}/decline`, { method: 'POST' })

/* --------------------------------------------------------------------------
   Share links, signature status and the signed PDF (Phase 2 items 6, 7, 9 —
   the authenticated side, surfaced by item 16a)
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} ShareLink
 * @property {string} id
 * @property {'remote'|'in_person'} capture_mode
 * @property {string} expires_at
 * @property {boolean} is_active
 * @property {string|null} revoked_at
 * @property {string} created_at
 * @property {string|null} last_viewed_at
 *
 * @typedef {ShareLink & {token: string, path: string}} IssuedShareLink
 */

/**
 * Issue a share link. THE PLAINTEXT TOKEN IS IN THIS RESPONSE AND NOWHERE ELSE
 * (D21) — it is stored hashed, so it cannot be shown again. Treat it like an
 * API key: surface it once, at creation, and make copying it the obvious move.
 *
 * `capture_mode` is decided HERE, at issuance, by an authenticated user. The
 * anonymous signing request can never claim a mode; it inherits the token's.
 *
 * @param {string} estimateId
 * @param {'remote'|'in_person'} [captureMode]
 * @returns {Promise<{share_link: IssuedShareLink}>}
 */
export const createShareLink = (estimateId, captureMode = 'remote') =>
  apiFetch(`/api/estimates/${estimateId}/share-links`, {
    method: 'POST',
    body: { capture_mode: captureMode }
  })

/** Non-secret metadata only — no tokens come back here. @returns {Promise<{share_links: ShareLink[]}>} */
export const getShareLinks = estimateId => apiFetch(`/api/estimates/${estimateId}/share-links`)

/** @returns {Promise<{revoked: boolean}>} */
export const revokeShareLink = (estimateId, linkId) =>
  apiFetch(`/api/estimates/${estimateId}/share-links/${linkId}/revoke`, { method: 'POST' })

/**
 * Mint a short-lived signed URL for the signed PDF (D18).
 *
 * The URL expires — fetch it at the moment of the click and navigate straight
 * to it, never cache it in state and never render it into a link that sits on
 * screen going stale.
 *
 * @returns {Promise<{url: string, expires_in: number}>}
 */
export const getSignedPdfUrl = estimateId => apiFetch(`/api/estimates/${estimateId}/signed-pdf`)

/* --------------------------------------------------------------------------
   Invoices
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} Invoice
 * @property {string} id
 * @property {string} job_id
 * @property {string|null} estimate_id
 * @property {string} invoice_number
 * @property {LineItem[]} line_items
 * @property {number} subtotal
 * @property {number} tax_rate
 * @property {number} tax_amount
 * @property {number} total
 * @property {number} amount_paid
 * @property {number} balance
 * @property {'draft'|'sent'|'partial'|'paid'|'overdue'|'written_off'|'voided'} status
 * @property {string|null} due_date
 * @property {string} payment_terms
 * @property {string|null} sent_at
 * @property {string} created_at
 */

/** @returns {Promise<{invoices: Invoice[]}>} */
export const getInvoices = (filters = {}) => apiFetch('/api/invoices', { query: filters })

/** @returns {Promise<{invoice: Invoice}>} */
export const getInvoice = id => apiFetch(`/api/invoices/${id}`)

/**
 * Create from an accepted estimate (line items are COPIED, not referenced), or
 * from a job with explicit line items. An unaccepted estimate answers 409
 * `estimate_not_accepted`.
 */
export const createInvoice = body => apiFetch('/api/invoices', { method: 'POST', body })

export const updateInvoice = (id, body) => apiFetch(`/api/invoices/${id}`, { method: 'PATCH', body })

export const sendInvoice = id => apiFetch(`/api/invoices/${id}/send`, { method: 'POST' })

/**
 * Manual status advance — the DOCUMENT statuses only (amended in Phase 2).
 *
 * `partial`, `paid` and `refunded` are DERIVED from the payment ledger and are
 * refused here with `status_derived_from_payments`. All three mean "money
 * moved by this much", and an amount belongs to a payment record. An invoice
 * becomes paid by recording the payment that paid it — `recordPayment` below.
 *
 * What is still set by hand: `sent`, `overdue`, `written_off`, `voided`.
 */
export const setInvoiceStatus = (id, status) =>
  apiFetch(`/api/invoices/${id}/status`, { method: 'POST', body: { status } })

/* --------------------------------------------------------------------------
   Payments (Phase 2 items 11 + 13)

   The dashboard NEVER touches card details. Recording a payment here is
   BOOKKEEPING for money collected somewhere else — a cheque, cash, a bank
   transfer. Copy on these screens must never imply MBOS charged anyone.
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} Payment
 * @property {string} id
 * @property {string} invoice_id
 * @property {number} amount              always POSITIVE, including reversals
 * @property {string} method              cash | check | card | ach | financing | other
 * @property {string} payment_type        deposit | milestone | final | adjustment | refund | void
 * @property {string|null} reference
 * @property {string|null} maverick_transaction_id  non-null = taken by the processor
 * @property {string|null} reverses_payment_id
 * @property {string} paid_at
 * @property {Payment[]} [reversals]              nested under the payment they reverse
 * @property {number} [refundable_remaining]      DISPLAY ONLY — the real cap is
 *                                                enforced inside the reversal
 *                                                transaction, not from this
 * @property {{mode: string, operation: string|null, available: boolean,
 *             action_label: string, unavailable_reason: string|null}} [reversal]
 */

/** @returns {Promise<{invoice: object, payments: Payment[], orphaned_reversals: Payment[]}>} */
export const getInvoicePayments = id => apiFetch(`/api/invoices/${id}/payments`)

/**
 * Record a manual/offline payment.
 *
 * `idempotency_key` is optional on this route and REQUIRED on a refund. Pass
 * one anyway: it makes a double-submitted form a no-op instead of a duplicate
 * row, and the server answers 200 with `duplicate: true` rather than 201.
 */
export const recordPayment = (invoiceId, body) =>
  apiFetch(`/api/invoices/${invoiceId}/payments`, { method: 'POST', body })

/**
 * Reverse a payment. OWNER ONLY — a non-owner receives 403 `not_permitted`.
 *
 * Record-only: no processor call is made, because Session 1 only reverses
 * payments that never went through one. A payment carrying a
 * `maverick_transaction_id` is refused with `processor_reversal_unavailable`,
 * and that guard is on the payment's own data rather than on this call.
 *
 * Omitting `amount` refunds everything still refundable on that payment.
 */
export const reversePayment = (paymentId, body) =>
  apiFetch(`/api/payments/${paymentId}/reverse`, { method: 'POST', body })

/** The amendment trail for one invoice (C16). Read-only and append-only. */
export const getInvoiceAmendments = id => apiFetch(`/api/invoices/${id}/amendments`)

/** The D25.3 balance invoice: what is signed for on a job minus what is billed. */
export const previewBalanceInvoice = jobId => apiFetch(`/api/jobs/${jobId}/balance-invoice`)

export const createBalanceInvoice = (jobId, body = {}) =>
  apiFetch(`/api/jobs/${jobId}/balance-invoice`, { method: 'POST', body })

/* --------------------------------------------------------------------------
   Scheduling
   -------------------------------------------------------------------------- */

/**
 * @typedef {object} Appointment
 * @property {string} id
 * @property {string|null} job_id
 * @property {string|null} assigned_user_id
 * @property {string} title
 * @property {string|null} description
 * @property {string} type
 * @property {'scheduled'|'confirmed'|'in_progress'|'completed'|'cancelled'|'no_show'} status
 * @property {string} scheduled_start
 * @property {string} scheduled_end
 * @property {string|null} address
 */

/**
 * @param {{from?: string, to?: string, job_id?: string, assigned_user_id?: string}} [params]
 * @returns {Promise<{appointments: Appointment[]}>}
 */
export const getAppointments = (params = {}) => apiFetch('/api/appointments', { query: params })

export const createAppointment = body => apiFetch('/api/appointments', { method: 'POST', body })

export const updateAppointment = (id, body) =>
  apiFetch(`/api/appointments/${id}`, { method: 'PATCH', body })

export const deleteAppointment = id => apiFetch(`/api/appointments/${id}`, { method: 'DELETE' })

/* ==========================================================================
   Document delivery (Phase 2 item 17)
   ========================================================================== */

export const sendEstimateEmail = (estimateId, body) =>
  apiFetch(`/api/estimates/${estimateId}/send-email`, { method: 'POST', body })

export const sendEstimateSms = (estimateId, body) =>
  apiFetch(`/api/estimates/${estimateId}/send-sms`, { method: 'POST', body })

export const sendInvoiceEmail = (invoiceId, body) =>
  apiFetch(`/api/invoices/${invoiceId}/send-email`, { method: 'POST', body })

export const sendInvoiceSms = (invoiceId, body) =>
  apiFetch(`/api/invoices/${invoiceId}/send-sms`, { method: 'POST', body })

export const getEstimateDeliveries = estimateId => apiFetch(`/api/estimates/${estimateId}/deliveries`)
export const getInvoiceDeliveries = invoiceId => apiFetch(`/api/invoices/${invoiceId}/deliveries`)

/* --------------------------------------------------------------------------
   Sending-domain verification (item 17)
   -------------------------------------------------------------------------- */

export const getSendingDomainStatus = () => apiFetch('/api/settings/sending-domain')

export const createSendingDomain = domain =>
  apiFetch('/api/settings/sending-domain', { method: 'POST', body: { domain } })

export const verifySendingDomain = () =>
  apiFetch('/api/settings/sending-domain/verify', { method: 'POST' })

/* ==========================================================================
   Notifications (Phase 2 item 19)
   ========================================================================== */

export const getNotifications = () => apiFetch('/api/notifications')
export const markNotificationRead = id => apiFetch(`/api/notifications/${id}/read`, { method: 'POST' })
export const markAllNotificationsRead = () => apiFetch('/api/notifications/read-all', { method: 'POST' })

export const getNotificationPreferences = () => apiFetch('/api/notification-preferences')
export const updateNotificationPreference = (type, body) =>
  apiFetch(`/api/notification-preferences/${type}`, { method: 'PATCH', body })

/* ==========================================================================
   The Operations zone (Phase 9 Session A — D9.1, D9.2, D9.3)

   Every call below is server-gated on the Operations grant and answers 403
   `operations_access_required` without it, whatever this client believes.
   ========================================================================== */

/**
 * @typedef {object} OperationsSection
 * @property {'reports'|'data_export'|'templates'|'payroll'} key
 * @property {string} path
 * @property {'live'|'planned'} state  a fact about this DEPLOYMENT's code, not
 *   about the tenant's tier — the four sections are not sold separately
 *
 * @typedef {object} OperationsOverview
 * @property {{granted: boolean, source: 'owner'|'grant', role: string|null}} access
 * @property {OperationsSection[]} sections
 */

/**
 * What this caller can reach inside Operations, and which sections are live.
 *
 * Carries NO metrics by design: reporting is Session B, and a zero-valued card
 * is indistinguishable from a real zero (the L2 flag-honesty rule).
 *
 * @returns {Promise<{operations: OperationsOverview}>}
 */
export const getOperationsOverview = () => apiFetch('/api/operations/overview')

/**
 * The basic Operations report (Phase 9 Session B).
 *
 * Date ranges are UTC calendar days. The API includes both boundary dates and
 * returns the exact timestamp window it used so edge records can be audited.
 *
 * @param {{range?: 'mtd'|'last_30_days'|'qtd'|'ytd'|'custom', start_date?: string, end_date?: string}} [filters]
 * @returns {Promise<{report: object}>}
 */
export const getOperationsReport = (filters = {}) =>
  apiFetch('/api/operations/reports', { query: filters })

/** @returns {Promise<{document_templates: object, configured: boolean, options: object}>} */
export const getDocumentTemplates = () => apiFetch('/api/operations/templates')

export const updateDocumentTemplates = documentTemplates =>
  apiFetch('/api/operations/templates', {
    method: 'PATCH',
    body: { document_templates: documentTemplates }
  })

/** The preview is the real server-rendered PDF, never a browser layout. */
export const previewDocumentTemplates = (documentType, documentTemplates) =>
  apiFetch('/api/operations/templates/preview', {
    method: 'POST',
    body: { document_type: documentType, document_templates: documentTemplates },
    responseType: 'blob'
  })

/* --------------------------------------------------------------------------
   Data export (Phase 2 item 21, RELOCATED into Operations by Phase 9 D9.2)

   Same handlers, new canonical path. The old /api/data-exports paths still
   answer and are gated identically, but nothing in the dashboard calls them.
   -------------------------------------------------------------------------- */

export const getDataExports = () => apiFetch('/api/operations/data-exports')
export const createDataExport = exportType =>
  apiFetch('/api/operations/data-exports', { method: 'POST', body: { export_type: exportType } })
export const getDataExportDownloadUrl = id =>
  apiFetch(`/api/operations/data-exports/${id}/download`)

/* ==========================================================================
   MWS Platform Console
   ========================================================================== */

export const getAdminMe = () => apiFetch('/api/admin/me')

export const getAdminApplications = (params = {}) =>
  apiFetch('/api/admin/applications', { query: params })

export const updateAdminApplication = (id, body) =>
  apiFetch(`/api/admin/applications/${id}`, { method: 'PATCH', body })

export const getAdminTenants = () => apiFetch('/api/admin/tenants')

export const provisionAdminTenant = body =>
  apiFetch('/api/admin/tenants/provision', { method: 'POST', body })

export const getAdminTenantClients = (tenantId, params = {}) =>
  apiFetch(`/api/admin/tenants/${tenantId}/clients`, { query: params })

export const createAdminTenantClient = (tenantId, body) =>
  apiFetch(`/api/admin/tenants/${tenantId}/clients`, { method: 'POST', body })

export const updateAdminTenantSms = (tenantId, body) =>
  apiFetch(`/api/admin/tenants/${tenantId}/sms`, { method: 'PATCH', body })

export const sendAdminTenantTestSms = (tenantId, to) =>
  apiFetch(`/api/admin/tenants/${tenantId}/sms/test`, { method: 'POST', body: { to } })

/**
 * Payroll gating (Phase 9 Part 1, D9.2). No test-send counterpart, and there
 * never will be: SurePayroll exposes no API to call.
 */
export const updateAdminTenantPayroll = (tenantId, body) =>
  apiFetch(`/api/admin/tenants/${tenantId}/payroll`, { method: 'PATCH', body })

/**
 * Merchant transaction-webhook completion (D41, build item 27). Like payroll
 * there is no test counterpart — but for a stronger reason than SurePayroll's
 * missing API. Maverick states outright that webhooks cannot be registered,
 * read or managed over the API even under Connect access, so there is nothing
 * to query even in principle.
 */
export const getAdminTenantMaverickWebhook = tenantId =>
  apiFetch(`/api/admin/tenants/${tenantId}/maverick-webhook`)

export const updateAdminTenantMaverickWebhook = (tenantId, body) =>
  apiFetch(`/api/admin/tenants/${tenantId}/maverick-webhook`, { method: 'PATCH', body })
